import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Checkbox,
    IconButton,
    Menu,
    MenuItem,
    Card,
    CardContent,
    FormControl,
    Chip,
    Button,
} from '@mui/material';
import { styled } from '@mui/system';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TablePagination from '@mui/material/TablePagination';
import AppTextField from './CustomTextField';
import AppButton from './CustomButton';
import { TableActionItem, TableColumn, TableFetchParams, TableTabItem } from '@/types';
import moment from 'moment';
import _ from 'lodash';
import { useLocation, useSearchParams } from 'react-router-dom';
import { enUS } from 'date-fns/locale';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css';
import { Reducer } from '@/utils/helpers';
import Select from 'react-select';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { datePresets, endOfDay, formatToCustomISO, parseDate, slaOptions, sourceOptions, startOfDay } from '@/utils/helpers';
import TableSkeletonRows from './TableSkeletonRows';
// Styled component for tabs
const StyledTab = styled(Box)({
    display: 'inline-flex',
    alignItems: 'center',
    color: '#121C2D',
    backgroundColor: '#fff',
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 500,
    minHeight: 34,
    padding: '4px 16px',
    border: '1px solid #8891AA',
    borderRadius: '100px',
    cursor: 'pointer',
    transition: '500ms',
    marginTop: 3,
    marginBottom: 3,
    '&:not(:last-child)': {
        marginRight: 6,
    },
    '&:hover': {
        color: '#030B5D',
        borderColor: '#030B5D',
        backgroundColor: '#CCE4FF',
    },
    '&.selected': {
        color: '#030B5D',
        borderColor: '#030B5D',
        backgroundColor: '#CCE4FF',
    },
});

// Styled Table Cell & Row
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(1),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    '& .MuiTableCell-root': {
        background: theme.palette.primary.main,
        color: '#fff !important',
        '& .MuiCheckbox-root': {
            '& .MuiSvgIcon-root': {
                fill: '#ccc',
            },
            '&.Mui-checked': {
                '& .MuiSvgIcon-root': {
                    fill: '#fff',
                },
            },
        },
    },
}));

// Props interface; fetchData callback now expects TableFetchParams
interface FilterOption {
    value: string | number;
    label: string;
}

export type FilterOptionSourceKey =
    | 'group'
    | 'member'
    | 'shift'
    | 'category'
    | 'subCategory'
    | 'subSubCategory'
    | 'dataClass'
    | 'salesperson';

export type FilterLoadingState = Partial<Record<FilterOptionSourceKey, boolean>>;
export type FilterErrorState = Partial<Record<FilterOptionSourceKey, string>>;

interface CustomTableProps {
    columns: TableColumn[];
    actions?: TableActionItem[];
    tableTabData?: TableTabItem[];
    onTabClick?: (item: TableTabItem, index: number) => void;
    onFilterClick?: () => void;
    onExportClick?: (event: React.MouseEvent<HTMLButtonElement>, params: TableFetchParams) => void;
    enableSelection?: boolean;
    enableExport?: boolean;
    allowedFilters?: ('date' | 'status' | 'source' | 'Required type' | 'sla' | 'slaStatus' | 'priority' | 'agentType' | 'category' | 'subCategory' | 'subSubCategory' | 'dataClass' | 'shift' | 'memberType' | 'group' | 'member' | 'assignment')[];
    statusOptions?: FilterOption[];
    slaStatusOptions?: FilterOption[];
    priorityOptions?: FilterOption[];
    shiftOptions?: FilterOption[];
    categoryFilterOptions?: FilterOption[];
    subCategoryFilterOptions?: FilterOption[];
    subSubCategoryFilterOptions?: FilterOption[];
    dataClassFilterOptions?: FilterOption[];
    agentTypeOptions?: { value: string; label: string }[];
    memberTypeOptions?: { value: string; label: string }[];
    groupFilterOptions?: FilterOption[];
    memberFilterOptions?: FilterOption[];
    assignmentOptions?: FilterOption[];
    filterLoading?: FilterLoadingState;
    filterErrors?: FilterErrorState;
    onGroupFilterChange?: (groupId: number | null) => void;
    formatDates?: boolean; // Whether to format dates in ISO format
    // fetchData function which returns a Promise resolving to { data, total }
    fetchData: (params: TableFetchParams) => Promise<{ data: any[]; total: number }>;
    hideSearch?: boolean;
    syncWithUrl?: boolean;
}


export interface CustomTableRef {
    refetch: () => void;
}

const initialstate = {
    dateRange: null,
    datePreset: null,
    status: null,
    slaStatus: null,
    priority: null,
    source: null,
    sla: null,
    agentType: null,
    memberType: null,
    groupId: null,
    memberId: null,
    assignment: null,
    category: null,
    subCategory: null,
    subSubCategory: null,
    dataClass: null,
    shift: null,
};

const parseFilters = (value: string): NonNullable<TableFetchParams['filters']> => {
    try {
        const filters = JSON.parse(value);
        return Array.isArray(filters) ? filters : [];
    } catch {
        return [];
    }
};

const getDatePresetValue = (
    dateFilter?: { startDate?: string | Date; endDate?: string | Date },
) => {
    if (!dateFilter?.startDate || !dateFilter?.endDate) return null;

    const parseStoredDate = (value: string | Date) =>
        value instanceof Date ? value : parseDate(value);
    const storedStart = parseStoredDate(dateFilter.startDate);
    const storedEnd = parseStoredDate(dateFilter.endDate);

    if (Number.isNaN(storedStart.getTime()) || Number.isNaN(storedEnd.getTime())) {
        return 'custom';
    }

    const matchingPreset = datePresets.find((preset) => {
        if (preset.value === 'custom') return false;
        const presetRange = preset.getRange();

        return startOfDay(storedStart).getTime() === startOfDay(presetRange.startDate).getTime()
            && endOfDay(storedEnd).getTime() === endOfDay(presetRange.endDate).getTime();
    });

    return matchingPreset?.value ?? 'custom';
};

const getFilterState = (filters: TableFetchParams['filters']) => {
    const appliedFilters = filters || [];
    const getValues = (key: string) => appliedFilters
        .filter(filter => filter?.[key] !== undefined)
        .map(filter => filter[key]);
    const getValue = (key: string) => getValues(key)[0] ?? null;
    const dateFilter = appliedFilters.find(filter => filter?.startDate && filter?.endDate);

    return {
        ...initialstate,
        dateRange: dateFilter
            ? [{
                startDate: parseDate(dateFilter.startDate),
                endDate: parseDate(dateFilter.endDate),
                key: 'selection',
            }]
            : null,
        datePreset: getDatePresetValue(dateFilter),
        status: getValues('status'),
        slaStatus: getValue('slaStatus'),
        priority: getValue('priority'),
        source: getValues('source'),
        sla: getValue('sla'),
        agentType: getValues('agentType'),
        memberType: getValues('memberType'),
        groupId: getValue('groupId'),
        memberId: getValue('memberId'),
        assignment: getValue('assignment'),
        category: getValue('ticketCategoryId'),
        subCategory: getValue('ticketSubCategoryId'),
        subSubCategory: getValue('ticketSubSubCategoryId'),
        dataClass: getValue('dataClass'),
        shift: getValue('shiftId'),
    };
};

const CustomTable = forwardRef<CustomTableRef, CustomTableProps>(({
    columns,
    actions,
    tableTabData,
    onTabClick,
    onFilterClick,
    onExportClick,
    enableSelection,
    enableExport,
    fetchData,
    allowedFilters,
    formatDates,
    statusOptions = [],
    slaStatusOptions = [],
    priorityOptions = [],
    shiftOptions = [],
    categoryFilterOptions = [],
    subCategoryFilterOptions = [],
    subSubCategoryFilterOptions = [],
    dataClassFilterOptions = [],
    agentTypeOptions = [],
    memberTypeOptions = [],
    groupFilterOptions = [],
    memberFilterOptions = [],
    assignmentOptions = [],
    filterLoading,
    filterErrors,
    onGroupFilterChange,
    hideSearch = false,
    syncWithUrl = true,
}, ref) => {
    // Use URL query params instead of local state
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
    const filterMenuOpen = Boolean(filterAnchorEl);

    // New state for handling live search input for debouncing.
    const [data, setData] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(true);
    const requestSequenceRef = useRef(0);

    // Internal state for selection and action menus.
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
    const [currentRow, setCurrentRow] = useState<any>(null);
    const [state, dispatch] = useReducer(Reducer, initialstate, () => ({
        ...initialstate
    }));
    const actionMenuOpen = Boolean(actionAnchorEl);
    // Member depends on a chosen group only where the drawer also offers the Group
    // filter. A table already scoped to one group (the group ticket queue) filters
    // by member on its own.
    const memberFilterEnabled = !!allowedFilters?.includes('member')
        && (!allowedFilters?.includes('group') || !!state.groupId);
    const urlParams = useMemo<TableFetchParams>(() => {
        const getParam = <T extends string>(key: string, fallback: T): T =>
            (searchParams.get(key) as T) || fallback;

        return {
            search: getParam('search', ''),
            page: parseInt(getParam('page', '0'), 10),
            limit: parseInt(getParam('limit', '10'), 10),
            sortField: getParam('sortField', 'updatedAt'),
            sortOrder: getParam('sortOrder', 'DESC'),
            filters: parseFilters(getParam('filters', '[]')),
        };
    }, [searchParams]);
    const [localParams, setLocalParams] = useState<TableFetchParams>({
        search: '',
        page: 0,
        limit: 10,
        sortField: 'updatedAt',
        sortOrder: 'DESC',
        filters: [],
    });
    const params = syncWithUrl ? urlParams : localParams;
    const [searchTerm, setSearchTerm] = useState<string>(
        syncWithUrl ? searchParams.get('search') || '' : '',
    );
    const { page, limit } = params;

    const updateParams = useCallback((newParams: Partial<TableFetchParams>) => {
        if (!syncWithUrl) {
            setLocalParams((current) => ({ ...current, ...newParams }));
            return;
        }

        const updated = { ...urlParams, ...newParams };
        const nextSearchParams = new URLSearchParams(searchParams);

        if (updated.search.trim()) {
            nextSearchParams.set('search', updated.search);
        } else {
            nextSearchParams.delete('search');
        }
        nextSearchParams.set('page', String(updated.page));
        nextSearchParams.set('limit', String(updated.limit));
        nextSearchParams.set('sortField', updated.sortField ?? 'updatedAt');
        nextSearchParams.set('sortOrder', updated.sortOrder ?? 'DESC');
        nextSearchParams.set('filters', JSON.stringify(updated.filters ?? []));
        setSearchParams(nextSearchParams, { state: location.state });
    }, [location.state, searchParams, setSearchParams, syncWithUrl, urlParams]);

    // Effect for debouncing the search input (500ms delay).
    useEffect(() => {
        const shouldRemoveEmptySearch = syncWithUrl
            && !searchTerm.trim()
            && searchParams.has('search');
        if (searchTerm === params.search && !shouldRemoveEmptySearch) return;

        const handler = setTimeout(() => {
            updateParams({ search: searchTerm, page: 0 });
        }, 500);
        return () => clearTimeout(handler);
    }, [params.search, searchParams, searchTerm, syncWithUrl, updateParams]);

    useEffect(() => {
        setSearchTerm(params.search);
    }, [params.search]);


    const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        dispatch({ type: 'RESET', payload: getFilterState(params.filters) });
        setFilterAnchorEl(event.currentTarget);
    };

    const applyFilters = () => {
        // Start by keeping only non-filterable values
        const newFilters = (params?.filters || []).filter(
            filter => !filter?.startDate
                && !filter?.status
                && !(allowedFilters?.includes('slaStatus') && filter?.slaStatus)
                && !(allowedFilters?.includes('priority') && filter?.priority)
                && !filter?.source
                && !filter?.agentType
                && !filter?.memberType
                && !filter?.groupId
                && !filter?.memberId
                && !(allowedFilters?.includes('assignment') && filter?.assignment)
                && !filter?.sla
                && !filter?.ticketCategoryId
                && !filter?.ticketSubCategoryId
                && !filter?.ticketSubSubCategoryId
                && !filter?.dataClass
                && !filter?.shiftId
        );

        // Apply date filter (if allowed and dates exist)
        if (allowedFilters?.includes('date') && state.dateRange?.[0]?.startDate && state.dateRange?.[0]?.endDate && state?.datePreset) {
            // Normalize to full-day boundaries so the range is inclusive and a
            // same-day selection isn't collapsed to a single midnight instant.
            const rangeStart = startOfDay(state.dateRange[0].startDate);
            const rangeEnd = endOfDay(state.dateRange[0].endDate);
            const newStartDate = formatDates
                ? formatToCustomISO(rangeStart)
                : rangeStart;
            const newEndDate = formatDates
                ? formatToCustomISO(rangeEnd)
                : rangeEnd;

            newFilters.push({
                startDate: newStartDate,
                endDate: newEndDate
            });
        }

        // Status filter (multi-select)
        if (allowedFilters?.includes('status') && state.status?.length) {
            state.status.forEach((status: string) => {
                newFilters.push({ status });
            });
        }

        // Source filter (multi-select)
        if (allowedFilters?.includes('source') && state.source?.length) {
            state.source.forEach((source: string) => {
                newFilters.push({ source });
            });
        };

        if (allowedFilters?.includes('agentType') && state.agentType?.length) {
            state.agentType.forEach((agentType: string) => {
                newFilters.push({ agentType });
            });
        }

        if (allowedFilters?.includes('memberType') && state.memberType?.length) {
            state.memberType.forEach((memberType: string) => {
                newFilters.push({ memberType });
            });
        }

        if (allowedFilters?.includes('slaStatus') && state.slaStatus) {
            newFilters.push({ slaStatus: state.slaStatus });
        }

        if (allowedFilters?.includes('priority') && state.priority) {
            newFilters.push({ priority: state.priority });
        }

        if (allowedFilters?.includes('group') && state.groupId) {
            newFilters.push({ groupId: state.groupId });
        }

        if (memberFilterEnabled && state.memberId) {
            newFilters.push({ memberId: state.memberId });
        }

        if (allowedFilters?.includes('assignment') && state.assignment) {
            newFilters.push({ assignment: state.assignment });
        }

        if (allowedFilters?.includes('sla') && state.sla) {
            newFilters.push({ sla: state.sla });
        }

        if (allowedFilters?.includes('category') && state.category) {
            newFilters.push({ ticketCategoryId: state.category });
        }

        if (allowedFilters?.includes('subCategory') && state.subCategory) {
            newFilters.push({ ticketSubCategoryId: state.subCategory });
        }

        if (allowedFilters?.includes('subSubCategory') && state.subSubCategory) {
            newFilters.push({ ticketSubSubCategoryId: state.subSubCategory });
        }

        if (allowedFilters?.includes('dataClass') && state.dataClass) {
            newFilters.push({ dataClass: state.dataClass });
        }

        if (allowedFilters?.includes('shift') && state.shift) {
            newFilters.push({ shiftId: state.shift });
        }

        updateParams({ filters: newFilters, page: 0 });
        setFilterAnchorEl(null);
    };

    const clearAllFilters = () => {
        dispatch({ type: 'RESET', payload: initialstate });
        onGroupFilterChange?.(null);
        updateParams({ filters: [], page: 0 });
        setFilterAnchorEl(null);
    };

    const fetchDataRef = useRef(fetchData);

    useEffect(() => {
        fetchDataRef.current = fetchData;
    }, [fetchData]);

    const loadData = useCallback(async () => {
        const requestSequence = ++requestSequenceRef.current;
        setIsLoading(true);

        try {
            const res = await fetchDataRef.current({
                ...params,
                page: params.page + 1, // Convert to 1-indexed if API requires it.
                skipGlobalLoader: true,
            });
            if (requestSequence === requestSequenceRef.current) {
                setData(res.data);
                setTotalCount(res.total);
            }
        } catch (error) {
            if (requestSequence === requestSequenceRef.current) {
                setData([]);
                setTotalCount(0);
                console.error('Error fetching table data:', error);
            }
        } finally {
            if (requestSequence === requestSequenceRef.current) {
                setIsLoading(false);
            }
        }
    }, [params]);

    useImperativeHandle(ref, () => ({
        refetch: loadData,
    }), [loadData]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Handler for live search input change.
    // This updates the searchTerm, which in turn is debounced.
    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        updateParams({ search: '', page: 0 });
    };

    const handleChangePage = (
        _: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => {
        updateParams({ page: newPage });
    };

    // Handler for rows per page change.
    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        updateParams({ limit: parseInt(event.target.value, 10), page: 0 });
    };

    // Handler for tab click which updates the filter value and resets page.
    const handleTabClick = (item: TableTabItem, index: number) => {
        const { key, value } = item;
        const filters = key && value ? [{ [key]: value }] : [];
        updateParams({ filters, page: 0 });
        if (onTabClick) onTabClick(item, index);
    };

    // Handler for sorting when table header is clicked.
    const handleSort = (col: TableColumn) => {
        if (!col.sortable) return;
        const field = col.sortField || col.accessor;
        updateParams({
            sortField: field,
            sortOrder:
                params.sortField === field && params.sortOrder === 'ASC'
                    ? 'DESC'
                    : 'ASC',
        });
    };

    // Export handlers.
    const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (onExportClick) {
            onExportClick(event, params);
        }
    };

    // Action menu handlers.
    const handleActionButtonClick = (event: React.MouseEvent<HTMLButtonElement>, row: any) => {
        setActionAnchorEl(event.currentTarget);
        setCurrentRow(row);
    };

    const handleActionMenuClose = () => {
        setActionAnchorEl(null);
        setCurrentRow(null);
    };

    const activeFilter = params.filters?.[0];

    const getDatesFromRanges = (ranges: any) => {
        const range = Object.values(ranges).find((r: any) => r?.startDate && r?.endDate);
        return range || { startDate: new Date(), endDate: new Date() } as { startDate: Date, endDate: Date };
    };


    const handleDateRangeChange = (ranges: any) => {
        const range = getDatesFromRanges(ranges);
        const { startDate, endDate } = range as { startDate: Date, endDate: Date };
        dispatch({
            type: 'SETDATA',
            payload: {
                name: 'dateRange',
                value: [{
                    startDate: startDate || undefined,
                    endDate: endDate || undefined,
                    key: 'selection'
                }]
            }
        });
    };

    const hasFilterChanges = () => {
        if (allowedFilters?.includes('date') && state.dateRange?.[0]?.startDate && state?.datePreset) {
            const currentDateFilter = {
                startDate: formatDates ? formatToCustomISO(startOfDay(state.dateRange[0].startDate)) : startOfDay(state.dateRange[0].startDate),
                endDate: formatDates ? formatToCustomISO(endOfDay(state.dateRange[0].endDate)) : endOfDay(state.dateRange[0].endDate)
            };

            const existingDateFilter = params.filters?.find(f => f.startDate);

            if (!existingDateFilter ||
                existingDateFilter.startDate !== currentDateFilter.startDate ||
                existingDateFilter.endDate !== currentDateFilter.endDate) {
                return true;
            }
        }

        // Status filter comparison (now multi-select)
        if (allowedFilters?.includes('status')) {
            const currentStatus = state.status || [];
            const existingStatus = params.filters
                ?.filter(f => f.status)
                .map(f => f.status) || [];

            if (currentStatus.length !== existingStatus.length ||
                !currentStatus.every((val: string) => existingStatus.includes(val)) ||
                !existingStatus.every((val: string) => currentStatus.includes(val))) {
                return true;
            }
        }

        // Source filter comparison (multi-select)
        if (allowedFilters?.includes('source')) {
            const currentSources = state.source || [];
            const existingSources = params.filters
                ?.filter(f => f.source)
                .map(f => f.source) || [];

            if (currentSources.length !== existingSources.length ||
                !currentSources.every((val: string) => existingSources.includes(val)) ||
                !existingSources.every((val: string) => currentSources.includes(val))) {
                return true;
            }
        }

        if (allowedFilters?.includes('agentType')) {
            const currentAgentTypes = state.agentType || [];
            const existingAgentTypes = params.filters
                ?.filter(f => f.agentType)
                .map(f => f.agentType) || [];

            if (currentAgentTypes.length !== existingAgentTypes.length ||
                !currentAgentTypes.every((val: string) => existingAgentTypes.includes(val)) ||
                !existingAgentTypes.every((val: string) => currentAgentTypes.includes(val))) {
                return true;
            }
        }

        if (allowedFilters?.includes('memberType')) {
            const currentMemberTypes = state.memberType || [];
            const existingMemberTypes = params.filters
                ?.filter(f => f.memberType)
                .map(f => f.memberType) || [];

            if (currentMemberTypes.length !== existingMemberTypes.length ||
                !currentMemberTypes.every((val: string) => existingMemberTypes.includes(val)) ||
                !existingMemberTypes.every((val: string) => currentMemberTypes.includes(val))) {
                return true;
            }
        }

        if (allowedFilters?.includes('slaStatus')) {
            const existingSlaStatus = params.filters
                ?.find(f => f.slaStatus)?.slaStatus ?? null;
            if (state.slaStatus !== existingSlaStatus) {
                return true;
            }
        }

        if (allowedFilters?.includes('priority')) {
            const existingPriority = params.filters
                ?.find(f => f.priority)?.priority ?? null;
            if (state.priority !== existingPriority) {
                return true;
            }
        }

        if (allowedFilters?.includes('group')) {
            const existingGroupId = params.filters?.find(f => f.groupId)?.groupId ?? null;
            if (state.groupId !== existingGroupId) {
                return true;
            }
        }

        if (allowedFilters?.includes('member')) {
            const existingMemberId = params.filters?.find(f => f.memberId)?.memberId ?? null;
            if (state.memberId !== existingMemberId) {
                return true;
            }
        }

        if (allowedFilters?.includes('assignment')) {
            const existingAssignment = params.filters?.find(f => f.assignment)?.assignment ?? null;
            if (state.assignment !== existingAssignment) {
                return true;
            }
        }

        if (allowedFilters?.includes('sla')) {
            const currentSla = state.sla;
            if (currentSla !== params.filters?.find(f => f.sla)?.sla) {
                return true;
            }
        }

        if (allowedFilters?.includes('category')) {
            const currentCategory = state.category;
            const existingCategory = params.filters?.find(f => f.ticketCategoryId)?.ticketCategoryId;
            if (currentCategory !== existingCategory) {
                return true;
            }
        }

        if (allowedFilters?.includes('subCategory')) {
            const currentSubCategory = state.subCategory;
            const existingSubCategory = params.filters?.find(f => f.ticketSubCategoryId)?.ticketSubCategoryId;
            if (currentSubCategory !== existingSubCategory) {
                return true;
            }
        }

        if (allowedFilters?.includes('subSubCategory')) {
            const currentSubSubCategory = state.subSubCategory;
            const existingSubSubCategory = params.filters?.find(f => f.ticketSubSubCategoryId)?.ticketSubSubCategoryId;
            if (currentSubSubCategory !== existingSubSubCategory) {
                return true;
            }
        }

        if (allowedFilters?.includes('dataClass')) {
            const currentDataClass = state.dataClass;
            const existingDataClass = params.filters?.find(f => f.dataClass)?.dataClass;
            if (currentDataClass !== existingDataClass) {
                return true;
            }
        }

        if (allowedFilters?.includes('shift')) {
            const currentShift = state.shift;
            const existingShift = params.filters?.find(f => f.shiftId)?.shiftId;
            if (currentShift !== existingShift) {
                return true;
            }
        }

        return false;
    };


    return (
        <Box>
            {/* Top Section with Tabs and Search */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', pb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>

                    {tableTabData?.map((item, idx) => {
                        const isActive = item.key
                            ? activeFilter?.[item.key] === item.value   // e.g. { status: 'open' }
                            : !activeFilter;                             // “All” when no filter

                        return (
                            <StyledTab
                                key={idx}
                                onClick={() => handleTabClick(item, idx)}
                                className={isActive ? 'selected' : ''}
                            >
                                {`${item.name} (${item.count || 0})`}
                            </StyledTab>
                        );
                    })}

                </Box>
                <Box sx={{ display: 'inline-flex', ml: 'auto' }}>
                    {hideSearch ? null : <FormControl sx={{ display: 'inline-flex', alignSelf: 'center', '&:not(:last-child)': { mr: 2 } }}>
                        <Box sx={{ display: 'inline-flex', position: 'relative', width: 276 }}>
                            <IconButton
                                type="submit"
                                aria-label="search"
                                sx={{
                                    display: 'inline-flex',
                                    position: 'absolute',
                                    top: 1,
                                    left: 2,
                                    zIndex: 1,
                                    width: 34,
                                    height: 34,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    background: 'transparent !important',
                                    borderRadius: '8px 0 0 8px',
                                }}
                            >
                                <SearchIcon fontSize="small" sx={{ color: '#606B85' }} />
                            </IconButton>
                            <AppTextField
                                onChange={(e) => handleSearchChange(e.target.value)}
                                label=" "
                                value={searchTerm}
                                placeholder="Search"
                                type="text"
                                InputProps={{
                                    endAdornment: searchTerm ? (
                                        <IconButton
                                            aria-label="Clear search"
                                            edge="end"
                                            size="small"
                                            onMouseDown={(event) => event.preventDefault()}
                                            onClick={handleClearSearch}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    ) : undefined,
                                }}
                                sx={{
                                    opacity: 1,
                                    '& .MuiInputBase-root': {
                                        marginTop: '0px !important',
                                        backgroundColor: '#fff !important',
                                        pl: 3.25,
                                        '& .MuiInputBase-input': {
                                            minHeight: '36px !important',
                                            py: '6px !important',
                                            fontSize: 14,
                                            backgroundColor: '#fff !important',
                                        },
                                    },
                                }}
                            />
                        </Box>
                    </FormControl>}
                    {onFilterClick && (
                        <AppButton
                            className="w-auto xs white"
                            sx={{ '&:not(:last-child)': { mr: 2 } }}
                            disableElevation
                            variant="outlined"
                            startIcon={<img src="/images/filter-icon.svg" alt="" />}
                            onClick={handleFilterClick}
                            aria-controls={filterMenuOpen ? 'filter-menu' : undefined}
                            aria-expanded={filterMenuOpen ? 'true' : undefined}
                        >
                            Filter
                            {/* Show badge if filters are applied */}
                            {(params?.filters && params?.filters?.length > 0) && (
                                <span style={{ marginLeft: '8px', background: '#030B5D', color: 'white', borderRadius: '10px', padding: '0px 8px', fontSize: '12px' }}>
                                    {params?.filters?.length || ""}
                                </span>
                            )}
                        </AppButton>
                    )}
                    {/* {onFilterClick && params?.filters && params?.filters?.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            {params?.filters && params?.filters.map((filter, index) => {
                                if (filter.startDate && filter.endDate) {
                                    return (
                                        <Chip
                                            key={`date-${index}`}
                                            label={`Date: ${format(parseDate(filter.startDate), 'MMM dd yyyy')} - ${format(parseDate(filter.endDate), 'MMM dd yyyy')}`}
                                            onDelete={() => {
                                                const newFilters = params?.filters?.filter((_, i) => i !== index) || [];
                                                updateParams({ filters: newFilters });
                                                dispatch({
                                                    type: 'SETDATA',
                                                    payload: {
                                                        name: 'datePreset',
                                                        value: null
                                                    }
                                                })

                                            }}
                                            deleteIcon={<Close fontSize="small" />}
                                        />
                                    );
                                }
                                if (filter.status) {
                                    const statusLabel = statusOptions.find(opt => opt.value === filter.status)?.label || filter.status;
                                    return (
                                        <Chip
                                            key={`status-${index}`}
                                            label={`Status: ${statusLabel}`}
                                            onDelete={() => {
                                                // Remove this specific status filter
                                                const newFilters = params.filters?.filter((_, i) => i !== index) || [];
                                                updateParams({ filters: newFilters });

                                                // Update the state to remove this status
                                                dispatch({
                                                    type: 'SETDATA',
                                                    payload: {
                                                        name: 'status',
                                                        value: state.status?.filter((s: string) => s !== filter.status) || []
                                                    }
                                                });
                                            }}
                                            deleteIcon={<Close fontSize="small" />}
                                            sx={{ margin: '0.25rem' }}
                                        />
                                    );
                                }
                                if (filter.source) {
                                    const sourceLabel = sourceOptions.find(opt => opt.value === filter.source)?.label || filter.source;
                                    return (
                                        <Chip
                                            key={`source-${index}`}
                                            label={`Source: ${sourceLabel}`}
                                            onDelete={() => {
                                                // Remove this specific source filter
                                                const newFilters = params.filters?.filter((_, i) => i !== index) || [];
                                                updateParams({ filters: newFilters });

                                                // Update the state to remove this source
                                                dispatch({
                                                    type: 'SETDATA',
                                                    payload: {
                                                        name: 'source',
                                                        value: state.source?.filter((s: string) => s !== filter.source) || []
                                                    }
                                                });
                                            }}
                                            deleteIcon={<Close fontSize="small" />}
                                            sx={{ margin: '0.25rem' }}
                                        />
                                    );
                                }
                                if (filter.agentType) {
                                    const agentTypeLabel = agentTypeOptions.find(opt => opt.value === filter.agentType)?.label || filter.agentType;
                                    return (
                                        <Chip
                                            key={`agentType-${index}`}
                                            label={`Agent Type: ${agentTypeLabel}`}
                                            onDelete={() => {
                                                const newFilters = params.filters?.filter((_, i) => i !== index) || [];
                                                updateParams({ filters: newFilters });

                                                dispatch({
                                                    type: 'SETDATA',
                                                    payload: {
                                                        name: 'agentType',
                                                        value: state.agentType?.filter((s: string) => s !== filter.agentType) || []
                                                    }
                                                });
                                            }}
                                            deleteIcon={<Close fontSize="small" />}
                                            sx={{ margin: '0.25rem' }}
                                        />
                                    );
                                }
                                if (filter.sla) {
                                    const slaLevel = slaOptions.find((opt) => opt.value === filter.sla)?.label || filter.sla;
                                    return (
                                        <Chip
                                            key={`sla-${index}`}
                                            label={`Sla: ${slaLevel}`}
                                            onDelete={() => {
                                                // Remove this specific source filter
                                                const newFilters = params.filters?.filter((_, i) => i !== index) || [];
                                                updateParams({ filters: newFilters });

                                                // Update the state to remove this source
                                                dispatch({
                                                    type: 'SETDATA',
                                                    payload: {
                                                        name: 'sla',
                                                        value: ''
                                                    }
                                                });
                                            }}
                                            deleteIcon={<Close fontSize="small" />}
                                            sx={{ margin: '0.25rem' }}
                                        />
                                    );
                                }
                                if (filter.ticketCategoryId) {
                                    const categoryName = categoryFilterOptions.find((opt) => opt.value === filter.ticketCategoryId)?.label || filter.ticketCategoryId;
                                    return (
                                        <Chip
                                            key={`category-${index}`}
                                            label={`Category: ${categoryName}`}
                                            onDelete={() => {
                                                // Remove this specific category filter
                                                const newFilters = params.filters?.filter((_, i) => i !== index) || [];
                                                updateParams({ filters: newFilters });

                                                // Update the state to remove this category
                                                dispatch({
                                                    type: 'SETDATA',
                                                    payload: {
                                                        name: 'category',
                                                        value: ''
                                                    }
                                                });
                                            }}
                                            deleteIcon={<Close fontSize="small" />}
                                            sx={{ margin: '0.25rem' }}
                                        />
                                    );
                                }
                                if (filter.ticketSubCategoryId) {
                                    const subCategoryName = subCategoryFilterOptions.find((opt) => opt.value === filter.ticketSubCategoryId)?.label || filter.ticketSubCategoryId;
                                    return (
                                        <Chip
                                            key={`subCategory-${index}`}
                                            label={`Sub Category: ${subCategoryName}`}
                                            onDelete={() => {
                                                const newFilters = params.filters?.filter((_, i) => i !== index) || [];
                                                updateParams({ filters: newFilters });

                                                dispatch({
                                                    type: 'SETDATA',
                                                    payload: {
                                                        name: 'subCategory',
                                                        value: ''
                                                    }
                                                });
                                            }}
                                            deleteIcon={<Close fontSize="small" />}
                                            sx={{ margin: '0.25rem' }}
                                        />
                                    );
                                }
                                if (filter.ticketSubSubCategoryId) {
                                    const subSubCategoryName = subSubCategoryFilterOptions.find((opt) => opt.value === filter.ticketSubSubCategoryId)?.label || filter.ticketSubSubCategoryId;
                                    return (
                                        <Chip
                                            key={`subSubCategory-${index}`}
                                            label={`Sub-Sub Category: ${subSubCategoryName}`}
                                            onDelete={() => {
                                                const newFilters = params.filters?.filter((_, i) => i !== index) || [];
                                                updateParams({ filters: newFilters });

                                                dispatch({
                                                    type: 'SETDATA',
                                                    payload: {
                                                        name: 'subSubCategory',
                                                        value: ''
                                                    }
                                                });
                                            }}
                                            deleteIcon={<Close fontSize="small" />}
                                            sx={{ margin: '0.25rem' }}
                                        />
                                    );
                                }
                                return null;
                            })}
                        </Box>
                    )} */}
                    <Menu
                        anchorEl={filterAnchorEl}
                        open={filterMenuOpen}
                        onClose={() => {
                            setFilterAnchorEl(null);
                            if (hasFilterChanges()) {
                                applyFilters(); // Only apply if there are actual changes
                            }
                        }}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        // PaperProps={{ style: { width: '360px', padding: '16px' } }}
                        sx={{
                            '& .MuiPaper-root': {
                                width: { xs: 'calc(100vw - 32px)', sm: 360 },
                                maxWidth: 'calc(100vw - 32px)',
                                // height: 360,
                                padding: 2
                            }
                        }}
                    >
                        {/* Date Filter (if allowed) */}
                        {allowedFilters?.includes('date') && (
                            <Box mb={2}>
                                <Box fontWeight="bold" mb={1}>Date Range</Box>
                                <Select
                                    options={datePresets}
                                    isClearable
                                    value={datePresets.find(preset => preset.value === state.datePreset) || null}
                                    onChange={(selectedOption) => {
                                        console.log(selectedOption, "selectedOption")
                                        if (!selectedOption) {
                                            dispatch({
                                                type: 'SETDATA',
                                                payload: {
                                                    name: 'datePreset',
                                                    value: null,
                                                }
                                            });
                                            dispatch({
                                                type: 'SETDATA',
                                                payload: {
                                                    name: 'dateRange',
                                                    value: null,
                                                }
                                            });
                                            return;
                                        }
                                        const preset = selectedOption as typeof datePresets[0];
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'dateRange',
                                                value: [preset.getRange()],
                                            },
                                        });
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'datePreset',
                                                value: preset.value,
                                            },
                                        });
                                    }}
                                    placeholder="Select date range..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                    }}
                                />

                                {/* Show DateRange picker only if "Custom" is selected */}
                                {state.datePreset === 'custom' && (
                                    <Box mt={2}>
                                        <DateRange
                                            editableDateInputs={true}
                                            onChange={handleDateRangeChange}
                                            moveRangeOnFirstSelection={false}
                                            ranges={state.dateRange}
                                            locale={enUS}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}

                        {allowedFilters?.includes('dataClass') && (
                            <Box mb={2}>
                                <Box fontWeight="bold" mb={1}>Data Class</Box>
                                <Select
                                    options={dataClassFilterOptions}
                                    value={dataClassFilterOptions.find(option =>
                                        option.value === state.dataClass
                                    ) || null}
                                    isClearable
                                    onChange={(selectedOption) => {
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'dataClass',
                                                value: selectedOption?.value ?? null
                                            }
                                        });
                                    }}
                                    placeholder="Select data class..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '36px',
                                            fontSize: '14px',
                                            '.css-1wa3eu0-placeholder': {
                                                color: '#606B85',
                                                fontSize: '14px'
                                            }
                                        })
                                    }}
                                />
                            </Box>
                        )}

                        {/* Status Filter (if allowed) */}
                        {allowedFilters?.includes('status') && (
                            <Box mb={2}>
                                <Box fontWeight="bold" mb={1}>Status</Box>
                                <Select
                                    options={statusOptions}
                                    value={statusOptions.filter(option =>
                                        state.status?.includes(option.value)
                                    )}
                                    isClearable
                                    isMulti
                                    onChange={(selectedOptions) => {
                                        const selectedStatus = selectedOptions?.map(option => option.value) || [];
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'status',
                                                value: selectedStatus
                                            }
                                        });
                                    }}
                                    placeholder="Select status..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '36px',
                                            fontSize: '14px',
                                            // Additional multi-select specific styles
                                            '.css-1wa3eu0-placeholder': {
                                                color: '#606B85',
                                                fontSize: '14px'
                                            }
                                        }),
                                        multiValue: (base) => ({
                                            ...base,
                                            backgroundColor: '#E6E8F0',
                                            borderRadius: '4px'
                                        })
                                    }}
                                />
                            </Box>
                        )}

                        {allowedFilters?.includes('shift') && (
                            <Box mb={2}>
                                <Box fontWeight="bold" mb={1}>Shift</Box>
                                <Select
                                    options={shiftOptions}
                                    isLoading={filterLoading?.shift === true}
                                    isDisabled={filterLoading?.shift === true}
                                    noOptionsMessage={() => filterErrors?.shift
                                        || (shiftOptions.length ? 'No matching shifts' : 'No shifts available')}
                                    value={shiftOptions.find(option =>
                                        option.value === state.shift
                                    ) || null}
                                    isClearable
                                    onChange={(selectedOption) => {
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'shift',
                                                value: selectedOption?.value ?? null
                                            }
                                        });
                                    }}
                                    placeholder="Select shift..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '36px',
                                            fontSize: '14px',
                                            '.css-1wa3eu0-placeholder': {
                                                color: '#606B85',
                                                fontSize: '14px'
                                            }
                                        })
                                    }}
                                />
                            </Box>
                        )}

                        {allowedFilters?.includes('category') && (
                            <Box mb={2}>
                                <Box fontWeight="bold" mb={1}>Category</Box>
                                <Select
                                    options={categoryFilterOptions}
                                    isLoading={filterLoading?.category === true}
                                    isDisabled={filterLoading?.category === true}
                                    noOptionsMessage={() => filterErrors?.category
                                        || (categoryFilterOptions.length ? 'No matching categories' : 'No categories available')}
                                    value={categoryFilterOptions.find(option =>
                                        option.value === state.category
                                    ) || null}
                                    isClearable
                                    onChange={(selectedOption) => {
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'category',
                                                value: selectedOption?.value ?? null
                                            }
                                        });
                                    }}
                                    placeholder="Select category..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '36px',
                                            fontSize: '14px',
                                            '.css-1wa3eu0-placeholder': {
                                                color: '#606B85',
                                                fontSize: '14px'
                                            }
                                        })
                                    }}
                                />
                            </Box>
                        )}

                        {allowedFilters?.includes('subCategory') && (
                            <Box mb={2}>
                                <Box fontWeight="bold" mb={1}>Sub Category</Box>
                                <Select
                                    options={subCategoryFilterOptions}
                                    isLoading={filterLoading?.subCategory === true}
                                    isDisabled={filterLoading?.subCategory === true}
                                    noOptionsMessage={() => filterErrors?.subCategory
                                        || (subCategoryFilterOptions.length ? 'No matching sub-categories' : 'No sub-categories available')}
                                    value={subCategoryFilterOptions.find(option =>
                                        option.value === state.subCategory
                                    ) || null}
                                    isClearable
                                    onChange={(selectedOption) => {
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'subCategory',
                                                value: selectedOption?.value ?? null
                                            }
                                        });
                                    }}
                                    placeholder="Select sub-category..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '36px',
                                            fontSize: '14px',
                                            '.css-1wa3eu0-placeholder': {
                                                color: '#606B85',
                                                fontSize: '14px'
                                            }
                                        })
                                    }}
                                />
                            </Box>
                        )}

                        {allowedFilters?.includes('subSubCategory') && (
                            <Box mb={2}>
                                <Box fontWeight="bold" mb={1}>Sub-Sub Category</Box>
                                <Select
                                    options={subSubCategoryFilterOptions}
                                    isLoading={filterLoading?.subSubCategory === true}
                                    isDisabled={filterLoading?.subSubCategory === true}
                                    noOptionsMessage={() => filterErrors?.subSubCategory
                                        || (subSubCategoryFilterOptions.length ? 'No matching sub-sub-categories' : 'No sub-sub-categories available')}
                                    value={subSubCategoryFilterOptions.find(option =>
                                        option.value === state.subSubCategory
                                    ) || null}
                                    isClearable
                                    onChange={(selectedOption) => {
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'subSubCategory',
                                                value: selectedOption?.value ?? null
                                            }
                                        });
                                    }}
                                    placeholder="Select sub-sub-category..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '36px',
                                            fontSize: '14px',
                                            '.css-1wa3eu0-placeholder': {
                                                color: '#606B85',
                                                fontSize: '14px'
                                            }
                                        })
                                    }}
                                />
                            </Box>
                        )}

                        {allowedFilters?.includes('source') && (
                            <Box mb={2}>
                                <Box fontWeight="bold" mb={1}>Source</Box>
                                <Select
                                    options={sourceOptions}
                                    value={sourceOptions.filter(option =>
                                        state.source?.includes(option.value)
                                    )}
                                    isClearable
                                    isMulti
                                    onChange={(selectedOptions) => {
                                        const selectedSource = selectedOptions?.map(option => option.value) || [];
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'source',
                                                value: selectedSource
                                            }
                                        });
                                    }}
                                    placeholder="Select source..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '36px',
                                            fontSize: '14px',
                                            // Additional multi-select specific styles
                                            '.css-1wa3eu0-placeholder': {
                                                color: '#606B85',
                                                fontSize: '14px'
                                            }
                                        }),
                                        multiValue: (base) => ({
                                            ...base,
                                            backgroundColor: '#E6E8F0',
                                            borderRadius: '4px'
                                        })
                                    }}
                                />
                            </Box>
                        )}

                        {allowedFilters?.includes('agentType') && (
                            <Box mb={2}>
                                <Box fontWeight="bold" mb={1}>Agent Type</Box>
                                <Select
                                    options={agentTypeOptions}
                                    value={agentTypeOptions.filter(option =>
                                        state.agentType?.includes(option.value)
                                    ) || null}
                                    isClearable
                                    isMulti
                                    onChange={(selectedOptions) => {
                                        const selectedAgentType = selectedOptions?.map(option => option.value) || [];
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'agentType',
                                                value: selectedAgentType
                                            }
                                        });
                                    }}
                                    placeholder="Select agent type..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '36px',
                                            fontSize: '14px',
                                            '.css-1wa3eu0-placeholder': {
                                                color: '#606B85',
                                                fontSize: '14px'
                                            }
                                        }),
                                        multiValue: (base) => ({
                                            ...base,
                                            backgroundColor: '#E6E8F0',
                                            borderRadius: '4px'
                                        })
                                    }}
                                />
                            </Box>
                        )}

                        {allowedFilters?.includes('memberType') && (
                            <Box mb={2}>
                                <Box fontWeight="bold" mb={1}>Member Type</Box>
                                <Select
                                    options={memberTypeOptions}
                                    value={memberTypeOptions.filter(option =>
                                        state.memberType?.includes(option.value)
                                    ) || null}
                                    isClearable
                                    isMulti
                                    onChange={(selectedOptions) => {
                                        const selectedMemberType = selectedOptions?.map(option => option.value) || [];
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'memberType',
                                                value: selectedMemberType
                                            }
                                        });
                                    }}
                                    placeholder="Select member type..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '36px',
                                            fontSize: '14px',
                                            '.css-1wa3eu0-placeholder': {
                                                color: '#606B85',
                                                fontSize: '14px'
                                            }
                                        }),
                                        multiValue: (base) => ({
                                            ...base,
                                            backgroundColor: '#E6E8F0',
                                            borderRadius: '4px'
                                        })
                                    }}
                                />
                            </Box>
                        )}

                        {allowedFilters?.includes('slaStatus') && (
                            <Box mb={2}>
                                <Box fontWeight="bold" mb={1}>SLA Status</Box>
                                <Select
                                    options={slaStatusOptions}
                                    value={slaStatusOptions.find(option =>
                                        option.value === state.slaStatus
                                    ) || null}
                                    isClearable
                                    onChange={(selectedOption) => {
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'slaStatus',
                                                value: selectedOption?.value ?? null
                                            }
                                        });
                                    }}
                                    placeholder="Select SLA status..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: base => ({ ...base, minHeight: '36px', fontSize: '14px' }),
                                    }}
                                />
                            </Box>
                        )}

                        {allowedFilters?.includes('priority') && (
                            <Box mb={2}>
                                <Box fontWeight="bold" mb={1}>Priority</Box>
                                <Select
                                    options={priorityOptions}
                                    value={priorityOptions.find(option =>
                                        option.value === state.priority
                                    ) || null}
                                    isClearable
                                    onChange={(selectedOption) => {
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'priority',
                                                value: selectedOption?.value ?? null
                                            }
                                        });
                                    }}
                                    placeholder="Select priority..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: base => ({ ...base, minHeight: '36px', fontSize: '14px' }),
                                    }}
                                />
                            </Box>
                        )}

                        {allowedFilters?.includes('group') && (
                            <Box mb={2}>
                                <Box fontWeight="bold" mb={1}>Group</Box>
                                <Select
                                    options={groupFilterOptions}
                                    isLoading={filterLoading?.group === true}
                                    isDisabled={filterLoading?.group === true}
                                    noOptionsMessage={() => filterErrors?.group
                                        || (groupFilterOptions.length ? 'No matching groups' : 'No groups available')}
                                    value={groupFilterOptions.find(option =>
                                        option.value === state.groupId
                                    ) || null}
                                    isClearable
                                    onChange={(selectedOption) => {
                                        const nextGroupId = selectedOption?.value ?? null;
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'groupId',
                                                value: nextGroupId
                                            }
                                        });
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'memberId',
                                                value: null
                                            }
                                        });
                                        onGroupFilterChange?.(
                                            typeof nextGroupId === 'number'
                                                ? nextGroupId
                                                : Number(nextGroupId) || null,
                                        );
                                    }}
                                    placeholder="Select group..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '36px',
                                            fontSize: '14px',
                                        }),
                                    }}
                                />
                            </Box>
                        )}

                        {allowedFilters?.includes('assignment') && (
                            <Box mb={2}>
                                <Box fontWeight="bold" mb={1}>Assignment</Box>
                                <Select
                                    options={assignmentOptions}
                                    value={assignmentOptions.find(option =>
                                        option.value === state.assignment
                                    ) || null}
                                    isClearable
                                    onChange={(selectedOption) => {
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'assignment',
                                                value: selectedOption?.value ?? null
                                            }
                                        });
                                    }}
                                    placeholder="Select assignment..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '36px',
                                            fontSize: '14px',
                                        }),
                                    }}
                                />
                            </Box>
                        )}

                        {memberFilterEnabled && (
                            <Box mb={2}>
                                <Box fontWeight="bold" mb={1}>Member</Box>
                                <Select
                                    options={memberFilterOptions}
                                    isLoading={filterLoading?.member === true}
                                    isDisabled={filterLoading?.member === true}
                                    noOptionsMessage={() => filterErrors?.member
                                        || (memberFilterOptions.length ? 'No matching members' : 'No members available')}
                                    value={memberFilterOptions.find(option =>
                                        option.value === state.memberId
                                    ) || null}
                                    isClearable
                                    onChange={(selectedOption) => {
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'memberId',
                                                value: selectedOption?.value ?? null
                                            }
                                        });
                                    }}
                                    placeholder="Select member..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '36px',
                                            fontSize: '14px',
                                        }),
                                    }}
                                />
                            </Box>
                        )}

                        {allowedFilters?.includes('sla') && (
                            <Box mb={2}>
                                <Box fontWeight="bold" mb={1}>SLA</Box>
                                <Select
                                    options={slaOptions}
                                    value={slaOptions.filter(option => option?.value === state.sla) || null}
                                    isClearable
                                    onChange={(selectedOptions) => {
                                        dispatch({
                                            type: 'SETDATA',
                                            payload: {
                                                name: 'sla',
                                                value: selectedOptions?.value
                                            }
                                        });
                                    }}
                                    placeholder="Select..."
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '36px',
                                            fontSize: '14px',
                                            '.css-1wa3eu0-placeholder': {
                                                color: '#606B85',
                                                fontSize: '14px'
                                            }
                                        }),
                                        multiValue: (base) => ({
                                            ...base,
                                            backgroundColor: '#E6E8F0',
                                            borderRadius: '4px'
                                        })
                                    }}
                                />
                            </Box>
                        )}

                        <Box display="flex" justifyContent="space-between" mt={12}>
                            <Button variant="text" onClick={clearAllFilters}>
                                Clear All
                            </Button>
                            <Button variant="contained" onClick={applyFilters}>
                                Apply
                            </Button>
                        </Box>
                    </Menu>
                    {enableExport && (
                        <AppButton
                            className="w-auto xs white"
                            sx={{ '&:not(:last-child)': { mr: 2 } }}
                            disableElevation
                            variant="outlined"
                            startIcon={<CloudDownloadIcon />}
                            // endIcon={<img src="/images/arrow-down.svg" alt="" />}
                            aria-haspopup="true"
                            onClick={handleExportClick}
                        >
                            Export
                        </AppButton>
                    )}
                    {/* <Menu
                        aria-labelledby="export-options"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        anchorEl={exportAnchorEl}
                        open={exportMenuOpen}
                        onClose={handleExportMenuClose}
                    >
                        <MenuItem onClick={handleExportMenuClose}>Export 1</MenuItem>
                        <MenuItem onClick={handleExportMenuClose}>Export 2</MenuItem>
                        <MenuItem onClick={handleExportMenuClose}>Export 3</MenuItem>
                    </Menu> */}
                </Box>
            </Box>

            {/* Table Content */}
            <Card variant="outlined" elevation={0} sx={{ boxShadow: 'none', p: 0 }}>
                <CardContent sx={{ p: 0 }}>
                    <TableContainer sx={{ maxHeight: 440 }}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <StyledTableRow>
                                    {enableSelection && (
                                        <StyledTableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={selectedRows.length > 0 && selectedRows.length < data.length}
                                                checked={data.length > 0 && selectedRows.length === data.length}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedRows(data.map((_, index) => index));
                                                    } else {
                                                        setSelectedRows([]);
                                                    }
                                                }}
                                            />
                                        </StyledTableCell>
                                    )}
                                    {columns.map((col, index) => (
                                        <StyledTableCell
                                            className="text-nowrap"
                                            key={index}
                                            onClick={() => handleSort(col)}
                                            style={{
                                                cursor: col.sortable ? 'pointer' : 'default',
                                                width: col.width,
                                                minWidth: col.minWidth,
                                            }}
                                        >
                                            {col.header}
                                            {col.sortable && (
                                                <>
                                                    {params.sortField === (col.sortField || col.accessor) ? (
                                                        params.sortOrder === 'ASC' ? ' ▲' : ' ▼'
                                                    ) : (
                                                        ' ⇅'
                                                    )}
                                                </>
                                            )}
                                        </StyledTableCell>
                                    ))}
                                    {actions && (
                                        <StyledTableCell sx={{ position: 'sticky', right: -1, zIndex: 3, width: 40 }}></StyledTableCell>
                                    )}
                                </StyledTableRow>
                            </TableHead>
                            <TableBody aria-busy={isLoading}>
                                {isLoading ? (
                                    <TableSkeletonRows
                                        columnCount={columns.length}
                                        rowCount={Math.min(Math.max(limit, 5), 10)}
                                        includeSelection={enableSelection}
                                        includeActions={Boolean(actions)}
                                    />
                                ) : data.length > 0 ? (
                                    data.map((row, rowIndex) => (
                                        <TableRow key={rowIndex}
                                            className={row?.createdAt && moment().diff(moment(row?.createdAt), 'minutes') < 5 ? 'Mui-selected' : row?.ticketSlaHistoryData?.length > 0 ? 'Mui-selected-red' : ''}
                                        >
                                            {enableSelection && (
                                                <StyledTableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedRows.includes(rowIndex)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedRows([...selectedRows, rowIndex]);
                                                            } else {
                                                                setSelectedRows(selectedRows.filter(id => id !== rowIndex));
                                                            }
                                                        }}
                                                    />
                                                </StyledTableCell>
                                            )}
                                            {columns.map((col, colIndex) => (
                                                <StyledTableCell
                                                    className="text-nowrap position-relative"
                                                    key={colIndex}
                                                    style={{
                                                        width: col.width,
                                                        minWidth: col.minWidth,
                                                    }}
                                                >
                                                    {/* {col.render ? col.render(row) : row[col.accessor]} */}
                                                    {col.render ? col.render(row) : _.get(row, col.accessor)}
                                                    {
                                                        colIndex === 0 &&
                                                        !!((row?.createdAt && moment().diff(moment(row?.createdAt), 'minutes') < 5) || (row?.updatedAt && moment().diff(moment(row?.updatedAt), 'minutes') < 5)) &&
                                                        <Chip size="small" label={row?.createdAt && moment().diff(moment(row?.createdAt), 'minutes') < 5 ? "New" : "Updated"} color="success" className="newChip" />
                                                    }
                                                </StyledTableCell>
                                            ))}
                                            {actions && (
                                                <StyledTableCell sx={{ position: 'sticky', right: -1, zIndex: 1, backgroundColor: 'background.paper' }}>
                                                    <IconButton
                                                        onClick={(e) => handleActionButtonClick(e, row)}
                                                        aria-controls={actionMenuOpen ? 'action-menu' : undefined}
                                                        aria-haspopup="true"
                                                        aria-expanded={actionMenuOpen ? 'true' : undefined}
                                                    >
                                                        <img src="/images/three-dot.svg" alt="options" />
                                                    </IconButton>
                                                </StyledTableCell>
                                            )}
                                        </TableRow>
                                    ))
                                ) : (
                                    // If there is no data, render a single row with a "No Data" message
                                    <TableRow>
                                        <StyledTableCell
                                            colSpan={
                                                // Calculate colspan: base columns + selection column if enabled + actions column if enabled.
                                                columns.length + (enableSelection ? 1 : 0) + (actions ? 1 : 0)
                                            }
                                            align="center"
                                            style={{ padding: '1.5rem' }}
                                        >
                                            <Box>No data available</Box>
                                        </StyledTableCell>
                                    </TableRow>
                                )}
                            </TableBody>

                        </Table>
                    </TableContainer>
                    {!isLoading && data.length > 0 && (
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            count={totalCount}
                            page={page}
                            rowsPerPage={limit}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            component="div"
                            sx={{ '& p': { marginBottom: 0 } }}
                        />
                    )}
                </CardContent>
            </Card>

            {/* Action Menu */}
            {actions && (
                <Menu
                    anchorEl={actionAnchorEl}
                    open={actionMenuOpen}
                    onClose={handleActionMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {currentRow && actions.map((action, idx) => {
                        // Check if the action should be shown based on the condition
                        if (action.showCondition && !action.showCondition(currentRow)) {
                            return null; // Skip rendering this action
                        }

                        const isDisabled = typeof action.disabled === 'function' ? action.disabled(currentRow) : Boolean(action.disabled);
                        const disabledReason = typeof action.disabledReason === 'function' ? action.disabledReason(currentRow) : action.disabledReason;

                        return (
                            <MenuItem
                                key={idx}
                                disabled={isDisabled}
                                title={isDisabled ? disabledReason : undefined}
                                sx={action.color ? { color: action.color } : undefined}
                                onClick={() => {
                                    if (isDisabled) return;
                                    action.handleAction(currentRow);
                                    handleActionMenuClose();
                                }}
                            >
                                {action.label}
                            </MenuItem>
                        );
                    })}
                </Menu>
            )}
        </Box>
    );
});

export default CustomTable;
